/**
 * Preview entity builders and property array definitions for each
 * geometry type. Used by the drafting system to generate live
 * preview entities during interactive command sessions.
 *
 * Extracted from simpleGeometry.ts — pure structural refactor.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
// Canonical Block Unit Factor — single source of truth for the INSUNITS
// conversion table (incl. 21 = US Survey Feet) and the block→drawing
// units ratio. The same function drives the INSERT auto-scale in
// `blockMutations.createBlockInsertEntityFull`, so the "Block Unit
// Factor" preview row always equals the scale the insert actually got.
// (#248 re-audit)
import { computeBlockUnitFactor } from "@dxf-ai-editor/drawing-model";
// Type vocabulary + numeric helpers live in `geometryEntityTypes.ts`
// — this previously imported from `simpleGeometry.ts`, which created a
// depcruise cycle (simpleGeometry.ts ↔ previewBuilders.ts).
import {
  computePolylineArea,
  computePolylineLength,
  formatNumber,
  pickCommonProperties,
  simpleGeometryCommonPreviewProperties,
  type SimpleGeometryEntity,
  type SimpleGeometryPoint,
  type SimpleEllipseEntity,
  type SimpleLwpolylineEntity,
  type SimpleTextEntity,
  type SimpleMtextEntity,
  type SimpleDimensionEntity,
  type SimpleMultileaderEntity,
  type SimpleRevisionCloudEntity,
  type SimpleHatchEntity,
  type SimpleBlockInsertEntity,
  type SimpleArrayRectEntity,
  type SimpleViewportEntity,
  type ExtendedGeometryPatch,
  type SimpleGeometryPropertyChange,
  type SimpleGeometryPropertySkip,
} from "./geometryEntityTypes";

// Re-export for back-compat — many consumers `import { simpleGeometryCommonPreviewProperties } from "./previewBuilders"`.
export { simpleGeometryCommonPreviewProperties };

/**
 * PR-9 — Derive annotative property rows for the Properties palette.
 *
 * Returns the 3 standard AutoCAD-parity rows shown for any annotative-
 * capable entity (TEXT/MTEXT/INSERT/HATCH/DIMENSION/MLEADER):
 *   - "Annotative"        → "Yes" / "No" — based on whether the entity
 *                           has any annotative context attached.
 *   - "Annotation scale"  → entity.creationScaleName ?? "—". This is
 *                           the scale active when the entity was created
 *                           (read-only display field per AutoCAD).
 *   - "Annotative scales" → comma-joined list of scale names from
 *                           annotativeContexts[]. The "..." button next
 *                           to this row in the Properties palette opens
 *                           AnnotativeObjectScaleDialog.
 *
 * The values are read from the inbound `properties` map; the per-entity
 * caller (`dxfGeometry.ts:buildXxxPreview`) is responsible for
 * populating the JSON-encoded `__entity_annotative_contexts` /
 * `__dimension_annotative_contexts` / `__mleader_annotative_contexts`
 * keys plus optionally a "Creation scale" key. This helper decodes
 * them; entities created live (drafted in command-engine flows) have
 * empty contexts and pass through with "No" / "—" / "" defaults.
 *
 * Property bag KEYS read (in order of preference):
 *   - `__entity_annotative_contexts` — TEXT/MTEXT/HATCH/INSERT/ATTDEF
 *   - `__dimension_annotative_contexts` — DIMENSION
 *   - `__mleader_annotative_contexts` — MLEADER
 *   - `Creation scale` — pre-formatted creation scale name
 */
export function deriveAnnotativeRows(
  properties: Record<string, string>
): Record<string, string> {
  const contextsJson =
    properties["__entity_annotative_contexts"] ??
    properties["__dimension_annotative_contexts"] ??
    properties["__mleader_annotative_contexts"] ??
    "[]";
  let scaleNames: string[] = [];
  try {
    const parsed = JSON.parse(contextsJson) as unknown;
    if (Array.isArray(parsed)) {
      scaleNames = parsed
        .map((c) => {
          if (typeof c === "object" && c != null && "scaleName" in c) {
            const name = (c as { scaleName?: unknown }).scaleName;
            return typeof name === "string" ? name : null;
          }
          return null;
        })
        .filter((n): n is string => n != null);
    }
  } catch {
    // Malformed JSON — surface as "no contexts" rather than throwing.
  }
  const annotative = scaleNames.length > 0 ? "Yes" : "No";
  const annotationScale = properties["Creation scale"] ?? "—";
  const annotativeScales = scaleNames.join(", ");
  return {
    Annotative: annotative,
    "Annotation scale": annotationScale,
    "Annotative scales": annotativeScales,
  };
}

export const simpleGeometryLinePreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Start X",
  "Start Y",
  // DXF LINE start/end carry a Z (group 30/31). Exposed for AutoCAD parity
  // with Point "Position Z" / Circle+Arc "Center Z" and so a 3-D line's
  // elevation is visible + editable in the Properties panel (residual R2 #317).
  "Start Z",
  "End X",
  "End Y",
  "End Z",
  "Length",
  "Angle"
] as const;

export const simpleGeometryPointPreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Position X",
  "Position Y",
  "Position Z",
  "Rotation"
] as const;

export const simpleGeometryCirclePreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Center X",
  "Center Y",
  "Center Z",
  "Radius",
  "Diameter",
  "Circumference",
  "Area"
] as const;

export const simpleGeometryArcPreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Center X",
  "Center Y",
  "Center Z",
  "Radius",
  "Start angle",
  "End angle",
  "Total angle",
  "Arc length"
] as const;

// ELLIPSE preview properties. AutoCAD "ellipse" Properties panel lists
// center + major-axis endpoint + ratio + start/end parameters so the
// user can pick an ellipse arc range; we expose the same set verbatim
// so the property palette is parity-clean.
export const ellipsePreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Center X",
  "Center Y",
  "Major axis X",
  "Major axis Y",
  "Ratio",
  "Start parameter",
  "End parameter"
] as const;

/* ── LWPOLYLINE preview properties ─────────────── */
export const lwpolylinePreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Vertices",
  "Closed",
  // DXF group 70 bit 128 (plinegen) — @codex P2 on c8394b70: the label became
  // shell-patchable + document-persisted (typed-writer-owned) and the
  // single-select bag always carries the key, but the MULTI-select Properties
  // palette is driven by `getSharedPropertiesForSelection(…, "properties")`,
  // which reads `editableInProperties` — seeded from THIS list
  // (`propertyCapabilities` LWPOLYLINE). Without the row here a 2×LWPOLYLINE
  // selection could not batch-edit plinegen at all.
  "Linetype generation",
  "Constant width",
  "Elevation",
  "Thickness",
  "Length",
  "Area"
] as const;

/* ── POLYLINE (legacy heavy) preview properties ── */
/**
 * AutoCAD's Properties palette for a heavy POLYLINE.
 *
 * ONE list covers all four subtypes because they are ONE DXF entity
 * discriminated by group 70 (AutoCAD itself relabels the palette header —
 * "2D Polyline" / "3D Polyline" / "Polygon Mesh" / "Polyface Mesh" — rather
 * than showing a different object). `Polyline type` is that header, surfaced
 * as a read-only row; the mesh rows read empty on a plain polyline exactly as
 * they do in AutoCAD.
 *
 * `Start segment width` / `End segment width` are the entity-level DEFAULTS
 * (DXF groups 40/41) that every vertex without its own 40/41 inherits — kept
 * as the two separate values the DXF has, not folded into one "Global width"
 * scalar that could not represent a tapered default.
 */
export const polylinePreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Polyline type",
  "Vertices",
  "Closed",
  "Linetype generation",
  "Start segment width",
  "End segment width",
  "Elevation",
  "Length",
  "Area",
  "M vertex count",
  "N vertex count",
  "Smooth surface M density",
  "Smooth surface N density",
  "Surface type",
] as const;

/* ── ACAD_TABLE preview properties ─────────────── */
/**
 * AutoCAD's Properties palette for a table, limited to the rows this change
 * actually owns.
 *
 * `Table style` is the REAL name read out of the referenced TABLESTYLE object
 * (`tableStyleLookup.ts`), never a hard-coded "Standard". Rows/Columns and the
 * row-height / column-width readouts come from the parsed AcDbTable header.
 *
 * Deliberately ABSENT: cell contents, cell styles, merges, table breaking and
 * the direction/flow controls — those are the table-EDITING feature (see
 * `tableEntitySchema`'s boundary doc), and advertising an editable row with no
 * typed home behind it is exactly the placeholder CLAUDE.md ПРИОРИТЕТ #1
 * forbids.
 */
export const tablePreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Table style",
  "Rows",
  "Columns",
  "Table width",
  "Table height",
  "Position X",
  "Position Y",
  "Position Z",
  "Rotation",
] as const;

/* ── OLE2FRAME preview properties ──────────────── */
/**
 * AutoCAD's Properties palette for an OLE object.
 *
 * The rows are the frame geometry plus the OLE record's own descriptors
 * (object type string, link/embed/static kind, draw aspect, payload size),
 * all parsed from the entity. The embedded picture itself is deliberately not
 * rendered (see `oleFrameEntitySchema`'s doc), which does not affect any row
 * here — the frame IS the entity's extent in AutoCAD too.
 */
export const oleFramePreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "OLE object type",
  "OLE version",
  "Draw aspect",
  "Upper left X",
  "Upper left Y",
  "Lower right X",
  "Lower right Y",
  "Width",
  "Height",
  "Data size",
] as const;

/* ── TEXT preview properties ───────────────────── */
export const textPreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Contents",
  "Text style",
  "Height",
  "Rotation",
  "Width factor",
  "Oblique angle",
  "Justify",
  "Insertion X",
  "Insertion Y",
  "Alignment X",
  "Alignment Y",
  "Thickness"
] as const;

/* ── MTEXT preview properties ──────────────────── */
export const mtextPreviewProperties = [
  "Color",
  "Layer",
  "Lineweight",
  "Transparency",
  "Plot Style",
  "Material",
  "Annotative",
  "Contents",
  "Text style",
  "Height",
  "Width",
  "Attachment",
  "Direction",
  "Rotation",
  "Line spacing",
  "Background fill",
  "Insertion X",
  "Insertion Y"
] as const;

/* ── DIMENSION preview properties ──────────────── */
export const dimensionPreviewProperties = [
  "Color",
  "Layer",
  "Linetype",
  "Linetype Scale",
  "Lineweight",
  "Transparency",
  "Plot Style",
  "Material",
  "Annotative",
  "Subtype",
  "Dimension style",
  "Text override",
  "Text height",
  "Text rotation",
  "Extension 1 X",
  "Extension 1 Y",
  "Extension 2 X",
  "Extension 2 Y",
  "Dimension line X",
  "Dimension line Y",
  "Third point X",
  "Third point Y",
  "Arc point X",
  "Arc point Y",
  "Oblique angle",
  "Leader length",
  "Anonymous block",
  "Measurement",
  "Angle"
] as const;

/* ── MULTILEADER preview properties ────────────── */
export const multileaderPreviewProperties = [
  "Color",
  "Layer",
  "Lineweight",
  "Transparency",
  "Plot Style",
  "Material",
  "Annotative",
  "Multileader style",
  "Contents",
  "Text height",
  "Arrow X",
  "Arrow Y",
  "Landing X",
  "Landing Y",
  "Arrowhead size",
  "Arrowhead block",
  "Text attachment",
  "Text direction",
  "Text frame",
  "Line spacing style",
  "Line spacing factor",
  "Leader color",
  "Leader weight",
  "Leader type",
  "Block name",
  "Block scale",
  "Block rotation",
  "Override flags",
  "Landing gap",
  "Leader count"
] as const;

/* ── REVISION CLOUD preview properties ─────────── */
export const revisionCloudPreviewProperties = [
  "Color",
  "Layer",
  "Lineweight",
  "Transparency",
  "Plot Style",
  "Material",
  "Annotative",
  "Cloud style",
  "Approximate arc length",
  "Corner 1 X",
  "Corner 1 Y",
  "Corner 2 X",
  "Corner 2 Y"
] as const;

/* ── HATCH preview properties ──────────────────── */
export const hatchPreviewProperties = [
  "Color",
  "Layer",
  "Lineweight",
  "Transparency",
  "Plot Style",
  "Material",
  "Annotative",
  "Pattern",
  "Scale",
  "Angle",
  "Associative",
  "Hatch style",
  "Pattern type",
  // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR — see the "Double" descriptor
  // (descriptors/containers.ts) for the AutoCAD-parity rationale (visible on
  // every hatch, read-only unless Pattern type = User defined).
  "Double"
] as const;

/* ── BLOCK INSERT preview properties ───────────── */
export const blockInsertPreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Name",
  "Insertion X",
  "Insertion Y",
  "Scale X",
  "Scale Y",
  "Scale Z",
  "Rotation",
  "Column count",
  "Row count",
  "Column spacing",
  "Row spacing",
  // BLOCK_RECORD / authored BlockDefinition metadata (see the patchable list
  // above for per-field mutability semantics).
  "Hyperlink",
  "Description",
  "Block Unit",
  "Block Unit Factor",
  "Allow Exploding",
  "Allow Non-Uniform Scale"
] as const;

/* ── RECTANGULAR ASSOCIATIVE ARRAY preview properties ── */
export const arrayRectPreviewProperties = [
  ...simpleGeometryCommonPreviewProperties,
  "Rows",
  "Columns",
  "Row spacing",
  "Column spacing",
  "Base X",
  "Base Y",
  "Axis angle",
  "Associative"
] as const;

/* ── VIEWPORT preview properties ───────────────── */
// "Shade plot" (DXF group 170) is intentionally absent: it is DISPLAY-ONLY in
// the PROPERTIES grid (the value projection in deriveViewportPreviewProperties
// STAYS, informational). Editing is via the dedicated ViewportInspector
// dropdown (command path: applyViewportSetShadePlot); MATCHPROP/Properties-apply
// copy is DEFERRED (viewport-special MATCHPROP-persistence subsystem — none of
// the six viewport-special fields persist via copy).
export const viewportPreviewProperties = [
  "Display lock",
  "Scale",
  "Layer",
  "Center X",
  "Center Y",
  "Width",
  "Height",
  "View height",
  "View center X",
  "View center Y",
  // @codex round-5 P2-2 — `View twist` (group 51) is a registry-derived OWNED plain
  // leaf (geometryReducer Step 1d writes viewTwistAngle), so it MUST be advertised
  // editable + a bag key here (was omitted → the Properties-grid row read as
  // unavailable/read-only though the document now writes it; the advertised∩owned
  // both-factories census + undo-lockstep need it). Same class as the Slice-A
  // plinegen editableInProperties gap.
  "View twist"
] as const;

/* ── Patchable property sets for new entity types ──── */
export const lwpolylinePatchableProperties = [
  "Closed",
  "Constant width",
  "Elevation",
  // S15 Slice A review round — DXF group 70 bit 128 (plinegen). The typed
  // writer (geometryReducer Step 1d) persists this label to the document, so
  // the SHELL must patch it too: without it the shell reducer reported the
  // same change "read-only or not patchable" while the document mutated —
  // zero changed shell keys → NULL undo inverse (un-undoable doc write) and a
  // lying audit summary. Also makes the MATCHPROP polyline-special plinegen
  // copy live (copyableInMatchpropSpecial, propertyCapabilities).
  "Linetype generation",
  "Thickness",
  // GRIPS-SUBSYSTEM (§5.11): the runtime preview bag
  // (dxfGeometry.buildLwpolylinePreview) stores "Vertices" as an
  // "x,y;x,y" coordinate string, and a vertex / segment-midpoint grip
  // drag rewrites that string through the Command Engine. Patchable so
  // the grip-drag commit lands; `applyExtendedEntityPropertyChanges`
  // copies the new string verbatim.
  "Vertices",
  "Bulges"
] as const;

export const splinePatchableProperties = [
  // GRIPS-SUBSYSTEM (§5.11): control-point / fit-point grip drags rewrite
  // these "x,y;x,y" coordinate strings through the Command Engine.
  "Control points",
  "Fit points",
  // Codex P2-6 (#317): a fit-point grip drag recomputes the knot vector, so
  // the UNDO inverse carries the prior "Knots" string — it must be patchable
  // so the restore lands on BOTH the shell preview and the document (the
  // document translator parses + applies it). Without this the shell preview
  // would diverge from the document on undo of a fit-grip recompute.
  "Knots"
] as const;

export const textPatchableProperties = [
  "Contents",
  "Text style",
  "Height",
  "Rotation",
  "Width factor",
  "Oblique angle",
  "Justify",
  "Insertion X",
  "Insertion Y",
  "Alignment X",
  "Alignment Y",
  "Thickness"
] as const;

export const mtextPatchableProperties = [
  "Contents",
  "Text style",
  "Height",
  "Width",
  "Attachment",
  "Direction",
  "Rotation",
  "Line spacing",
  "Background fill",
  "Insertion X",
  "Insertion Y"
] as const;

export const dimensionPatchableProperties = [
  "Dimension style",
  "Text override",
  "Text height",
  // S15 Slice C — Text rotation / Oblique angle / Leader length are typed-writer
  // OWNED (geometryReducer Step 1d persists them) and were writable via the
  // legacy translator map too; without them in the shell-patchable set the shell
  // applier SKIPPED them while the document wrote them → shell↔document
  // divergence. Added so the shell mirrors every owned Dimension leaf.
  "Text rotation",
  "Oblique angle",
  "Leader length",
  "Extension 1 X",
  "Extension 1 Y",
  "Extension 2 X",
  "Extension 2 Y",
  "Dimension line X",
  "Dimension line Y",
  "Angle"
] as const;

export const multileaderPatchableProperties = [
  "Multileader style",
  "Contents",
  "Text height",
  "Arrow X",
  "Arrow Y",
  "Landing X",
  "Landing Y",
  "Arrowhead size",
  "Arrowhead block",
  "Text attachment",
  "Text direction",
  "Text frame",
  "Line spacing style",
  "Line spacing factor",
  "Leader color",
  "Leader weight",
  "Leader type",
  "Block name",
  "Block scale",
  "Block rotation",
  "Landing gap"
] as const;

export const revisionCloudPatchableProperties = [
  "Cloud style",
  "Approximate arc length",
  // REVCLOUD-PROPS-EDITOR (§5.1, @codex #487 P3) — the special editor also edits
  // Minimum arc length + Creation mode, and the document translator now persists
  // both (labelToFieldByType["Revision Cloud"]). Without them in the SHELL
  // patchable set, a Creation-mode-only or minimum-arc-only Apply persisted to the
  // document but the shell reported "no applicable property changes", surfacing a
  // valid edit as an audit WARNING (shell↔document parity gap). Listing them keeps
  // the shell accept-set aligned with the translator write-set.
  "Minimum arc length",
  "Creation mode",
  "Corner 1 X",
  "Corner 1 Y",
  "Corner 2 X",
  "Corner 2 Y"
] as const;

export const hatchPatchableProperties = [
  "Pattern",
  "Scale",
  "Angle",
  "Associative",
  "Hatch style",
  "Pattern type",
  // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR — shell-patchable leg of the
  // BAG / SHELL / DOCUMENT triangle (propertyTranslator.ts specialBranch-
  // DocumentLabels comment); the per-entity readOnly gate (Pattern type !=
  // User defined) is enforced downstream by the typed writer, not here.
  "Double"
] as const;

export const blockInsertPatchableProperties = [
  "Name",
  "Insertion X",
  "Insertion Y",
  "Scale X",
  "Scale Y",
  "Scale Z",
  "Rotation",
  "Column count",
  "Row count",
  "Column spacing",
  "Row spacing",
  // ── BLOCK_RECORD / authored BlockDefinition metadata (Phase 1 — read-only in
  // the property palette; BEDIT in Phase 2 will make them writable via the
  // block authoring workflow). `Hyperlink` is the only mutable new field:
  // AutoCAD lets the user edit per-insert hyperlinks on existing blocks, so we
  // keep it in the patchable set while the block-record-sourced properties
  // stay read-only.
  "Hyperlink",
  "Description",
  "Block Unit",
  "Block Unit Factor",
  "Allow Exploding",
  "Allow Non-Uniform Scale"
] as const;

/** Subset of `blockInsertPatchableProperties` that the property palette is
 * allowed to actually write back. The remaining entries are rendered in the
 * palette (for read-only display) but reject edits at the patch boundary — a
 * Phase 2 BEDIT flow will change them via the block authoring workflow, not a
 * direct INSERT property edit. */
export const blockInsertReadOnlyProperties = new Set<string>([
  "Description",
  "Block Unit",
  "Block Unit Factor",
  "Allow Exploding",
  "Allow Non-Uniform Scale",
]);

export const arrayRectPatchableProperties = [
  "Rows",
  "Columns",
  "Row spacing",
  "Column spacing",
  "Base X",
  "Base Y",
  "Axis angle",
  "Associative"
] as const;

// "Shade plot" is NOT patchable: display-only in the grid / MATCHPROP, edited via
// the ViewportInspector dropdown command path; viewport-special MATCHPROP-persist
// deferred (family-wide subsystem).
export const viewportPatchableProperties = [
  "Display lock",
  "Scale",
  "Layer",
  "Center X",
  "Center Y",
  "Width",
  "Height",
  "View height",
  "View center X",
  "View center Y",
  // @codex round-5 P2-2 — View twist (group 51) is a persisted OWNED plain leaf
  // (Step 1d writes viewTwistAngle), so it is shell-patchable too (mirrors the
  // viewportPreviewProperties advertise fix above).
  "View twist"
] as const;

export const simpleGeometryLinePatchableProperties = [
  "Start X",
  "Start Y",
  // Start Z / End Z are patchable so a grip-drag-emitted Z (sourced raw from
  // the document by gripDragTranslate) and a direct Properties-panel Z edit
  // both reach the document — AutoCAD LINE exposes editable Z (residual R2 #317).
  "Start Z",
  "End X",
  "End Y",
  "End Z"
] as const;

export const simpleGeometryCirclePatchableProperties = [
  "Center X",
  "Center Y",
  // Center Z is patchable so a direct Properties-panel Z edit reaches the
  // document — AutoCAD CIRCLE exposes an editable group-30 Z. Without this the
  // shown "Center Z" row was silently dropped as read-only and a 2-D Center X/Y
  // edit flattened a non-zero elevation (residual R2 #317, mirror of LINE Z).
  "Center Z",
  "Radius"
] as const;

export const simpleGeometryArcPatchableProperties = [
  "Center X",
  "Center Y",
  // Center Z patchable — same group-30 Z contract as CIRCLE (residual R2 #317).
  "Center Z",
  "Radius",
  "Start angle",
  "End angle"
] as const;

export const patchablePropertyLookup: Record<SimpleGeometryEntity["type"], ReadonlySet<string>> = {
  Line: new Set(simpleGeometryLinePatchableProperties),
  Circle: new Set(simpleGeometryCirclePatchableProperties),
  Arc: new Set(simpleGeometryArcPatchableProperties)
};

// ── S15 Slice B — Point + Ellipse first-time shell writability ──────────────
// These two kinds route through `applyExtendedEntityPropertyChanges` (NOT the
// Line/Circle/Arc simple-geometry applier), so their patchable sets live in
// `extendedPatchableLookup` below. The label sets mirror the typed descriptor
// registry's owned labels (`getTypedExtendedGeometryOwnedLabels` — geometryReducer
// Step 1d really persists every one of them); the typed shell mirror
// (`typedExtendedGeometryShellRejects`) then rejects on the bag exactly what the
// document write rejects (Ratio ∉ (0,1], non-finite numerics, …), so shell and
// document move in lockstep. Grip drag is ALSO wired for both kinds — POINT's
// single "location" grip (S12b) and ELLIPSE's center/major-axis/minor-axis
// grips (GRIPS-DRAG-MORE-TYPES, `gripDragTranslate.ts`) — reusing these exact
// patchable label sets, so Properties-text-edit and grip-drag stay in lockstep
// for every label here.
export const pointPatchableProperties = [
  "Position X",
  "Position Y",
  // Group-30 Z — editable per AutoCAD POINT parity (mirror of LINE Start/End Z
  // + CIRCLE/ARC Center Z).
  "Position Z",
  // Group 50 — stored radians, displayed degrees (descriptor toRaw converts).
  "Rotation"
] as const;

export const ellipsePatchableProperties = [
  "Center X",
  "Center Y",
  // Group 11/21 — the major-axis endpoint VECTOR relative to center.
  "Major axis X",
  "Major axis Y",
  // Group 40 — minor/major ratio, AutoCAD bound (0, 1] (descriptor exclusiveMin
  // 0 + max 1 is the gate; the schema's plain .positive() stays looser).
  "Ratio",
  // Groups 41/42 — start/end parameters in RADIANS (displayed raw).
  "Start parameter",
  "End parameter"
] as const;

/* ── Legacy heavy POLYLINE / ACAD_TABLE / OLE2FRAME patchable sets ─── */
/**
 * @codex F1 (P1) — the SHELL half of the legacy-polyline/table/OLE property
 * routing. Each list is EXACTLY the typed-writer-owned label set for its kind
 * (`getTypedExtendedGeometryOwnedLabels`, registry-derived — the labels
 * geometryReducer Step 1d really persists), and matches the `editableProperties`
 * both preview factories advertise. Being in `extendedPatchableLookup` is what
 * routes the kind to `applyExtendedEntityPropertyChanges` (and therefore through
 * the typed shell mirror `typedExtendedGeometryShellRejects`) instead of the
 * generic `applyChangesToEntityBag` tail, which accepted ANY advertised row into
 * the bag — including the read-only ones — while the document ignored all of
 * them. Read-only rows (Vertices / mesh grid / Length / Area / Rows / Columns /
 * Rotation / the OLE record descriptors) are deliberately ABSENT: they now skip
 * as `derived`, exactly like LWPOLYLINE Length/Area.
 */
export const polylinePatchableProperties = [
  // DXF group 70 bits 1 and 128 — the descriptor folds the edited bit back into
  // the whole flags integer, so the other six bits survive.
  "Closed",
  "Linetype generation",
  // Groups 40/41 — the entity-level DEFAULT widths every vertex without its own
  // 40/41 inherits (kept as the two separate DXF values, never one scalar).
  "Start segment width",
  "End segment width",
  // The group-30 Z of the 10/20/30 dummy point IS the elevation per the DXF
  // reference; X/Y are structurally 0 and are not surfaced.
  "Elevation"
] as const;

export const tablePatchableProperties = [
  // Group 10/20/30 — the insertion point. The ONLY writable table row: cell
  // values, formulas, styles, merges, TABLESTYLE management and table breaking
  // are the deferred table-editing feature (see `tableEntitySchema`'s boundary
  // doc), and `Rotation` is a derived read-out of the 11/21/31 horizontal
  // -direction VECTOR, not an independently writable angle.
  "Position X",
  "Position Y",
  "Position Z"
] as const;

export const oleFramePatchableProperties = [
  // Groups 10/20 + 11/21 — the frame corners. The frame IS the entity's whole
  // geometry (the embedded payload is never decoded), and AutoCAD's corner grips
  // edit exactly these; Width / Height are derived read-outs of the two corners.
  "Upper left X",
  "Upper left Y",
  "Lower right X",
  "Lower right Y"
] as const;

// Hoisted from `simpleGeometry.ts` so `extendedPropertyMutations.ts`
// can import it without re-importing simpleGeometry (was a depcruise
// cycle source).
export const extendedPatchableLookup: Record<string, ReadonlySet<string>> = {
  // S15 Slice B — Point + Ellipse first-time writability (see the note above
  // `pointPatchableProperties`).
  Point: new Set(pointPatchableProperties),
  Ellipse: new Set(ellipsePatchableProperties),
  // @codex F1 — legacy heavy POLYLINE / ACAD_TABLE / OLE2FRAME (see above).
  Polyline: new Set(polylinePatchableProperties),
  Table: new Set(tablePatchableProperties),
  "OLE Frame": new Set(oleFramePatchableProperties),
  LWPOLYLINE: new Set(lwpolylinePatchableProperties),
  Spline: new Set(splinePatchableProperties),
  Text: new Set(textPatchableProperties),
  MText: new Set(mtextPatchableProperties),
  Dimension: new Set(dimensionPatchableProperties),
  Multileader: new Set(multileaderPatchableProperties),
  "Revision Cloud": new Set(revisionCloudPatchableProperties),
  Hatch: new Set(hatchPatchableProperties),
  Block: new Set(blockInsertPatchableProperties),
  "Rectangular Associative Array": new Set(arrayRectPatchableProperties),
  Viewport: new Set(viewportPatchableProperties)
};

export function isExtendedPatchableProperty(
  entityType: string,
  propertyName: string
): boolean {
  return extendedPatchableLookup[entityType]?.has(propertyName) ?? false;
}

export function computeDimensionMeasurement(
  ext1: SimpleGeometryPoint,
  ext2: SimpleGeometryPoint
): number {
  const dx = ext2.x - ext1.x;
  const dy = ext2.y - ext1.y;
  return Math.hypot(dx, dy);
}

/* ── LWPOLYLINE derivation and build ───────────────── */

export function deriveLwpolylinePreviewProperties(
  entity: SimpleLwpolylineEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  const length = computePolylineLength(entity.vertices, entity.closed);
  const area = computePolylineArea(entity.vertices, entity.closed);

  return {
    ...baseProperties,
    // Codex P2 (local review, batch-5 follow-up): this used to serialize
    // `String(entity.vertices.length)` (a bare COUNT, e.g. "3") instead of
    // the `"x,y;x,y;…"` coordinate-list convention the sibling import-path
    // builder (`dxfGeometry.buildLwpolylinePreview`) already establishes as
    // the one-truth `Vertices` format — `readPointList` (the scene layer's
    // shared point-list parser, used by `resolveEntityPreviewCoordinate`
    // AND the grip/vertex-drag machinery) parses a bare count as `[]` (no
    // comma to split an x/y pair on), so a command-engine-CREATED or
    // BLOCK-preview LWPOLYLINE (this factory's own callers) could never be
    // selected for TRIM, EXTEND, or BACTION through the primary
    // interactive point-list fallback at all, even though the identical
    // entity IMPORTED from a DXF file (going through `dxfGeometry`
    // instead) worked correctly.
    Vertices: entity.vertices.map((v) => `${formatNumber(v.x)},${formatNumber(v.y)}`).join(";"),
    // Matches `dxfGeometry.buildLwpolylinePreview`'s own `Bulges` sibling —
    // `resolveEntityPreviewCoordinate`'s round-20 bulge-hit-testing fix
    // reads this to resolve a click on a BULGED (arc) segment against its
    // true arc, not a straight chord; without it, every segment of a
    // command-engine-created polyline was silently treated as straight.
    Bulges: entity.vertices.map((v) => formatNumber(v.bulge ?? 0)).join(";"),
    Closed: entity.closed ? "Yes" : "No",
    // @codex P2 round-3 (0847142a) — sibling site of the import-path builder
    // (`dxfGeometry.buildLwpolylinePreview`): the bag must ALWAYS carry the
    // plinegen key in the canonical "true"/"false" form. Without it a
    // command-engine-CREATED polyline (this factory) had no single-select
    // Properties row (rows come from `Object.entries(entity.properties)`) and
    // MATCHPROP could neither display/copy plinegen from a default source nor
    // CLEAR a plinegen=true target (the resolution copies only labels present
    // in `sourceEntity.properties`). `plinegen` is optional on the typed
    // entity (absent ⇒ false — same contract as the drawing-model schema).
    // Elevation / Constant width (the other two newly-persisting labels) were
    // already emitted below — plinegen was the ONE missing sibling.
    "Linetype generation": entity.plinegen ? "true" : "false",
    "Constant width": formatNumber(entity.constantWidth),
    Elevation: formatNumber(entity.elevation),
    Thickness: formatNumber(entity.thickness),
    Length: formatNumber(length),
    Area: formatNumber(area)
  };
}

export function buildLwpolylinePreviewEntity(
  entity: SimpleLwpolylineEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveLwpolylinePreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `LWPOLYLINE ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "LWPOLYLINE created through the geometry command core.",
    editableProperties: [...lwpolylinePreviewProperties],
    properties
  };
}

/* ── TEXT derivation and build ──────────────────────── */

// Decode the (horizontal, vertical) TEXT justification codes into the AutoCAD
// palette label (e.g. "Baseline Left"). Exported as the ONE TRUTH the typed
// property descriptors reuse for the Text "Justify" display (bag-identical with
// the production single-select builder) — no re-implementation.
export function justifyLabel(h: number, v: number): string {
  const hLabels = ["Left", "Center", "Right", "Aligned", "Middle", "Fit"];
  const vLabels = ["Baseline", "Bottom", "Middle", "Top"];
  return `${vLabels[v] ?? "Baseline"} ${hLabels[h] ?? "Left"}`;
}

export function deriveTextPreviewProperties(
  entity: SimpleTextEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  // PR-9 — annotative rows (Annotative / Annotation scale / Annotative
  // scales). Override the simple "No" default in the spread below.
  const annotativeRows = deriveAnnotativeRows(properties);
  return {
    ...baseProperties,
    Contents: entity.contents,
    "Text style": entity.styleName,
    Height: formatNumber(entity.height),
    Rotation: formatNumber(entity.rotation),
    "Width factor": formatNumber(entity.widthFactor),
    "Oblique angle": formatNumber(entity.obliqueAngle),
    Justify: justifyLabel(entity.hJustification, entity.vJustification),
    "Insertion X": formatNumber(entity.insertionPoint.x),
    "Insertion Y": formatNumber(entity.insertionPoint.y),
    "Alignment X": formatNumber(entity.alignmentPoint?.x ?? entity.insertionPoint.x),
    "Alignment Y": formatNumber(entity.alignmentPoint?.y ?? entity.insertionPoint.y),
    Thickness: formatNumber(entity.thickness),
    // GitHub Codex review PR #558 round 2 (round-3 local read-only follow-up)
    // P2 — `state/dxfGeometry.ts`'s persisted-document TEXT preview builder
    // already writes this row (read by `textToRenderable.ts`'s live mesh
    // path — see its own "Generation flags" doc comment); this shell-preview
    // (optimistic command-engine `EntityPreview`, e.g. EXPLODE's live
    // preview before commit) lacked it, so a MIRRTEXT-mirrored exploded
    // ATTRIB/TEXT rendered unmirrored in the preview even though the
    // persisted reducer output (`blockMutations.ts`'s `SimpleTextEntity.
    // generationFlags`) was already correct. Not in `textPreviewProperties`
    // (editableProperties) — read-only display row, matching the persisted
    // path's OWN editableProperties list (which also omits it).
    "Generation flags": formatNumber(entity.generationFlags ?? 0),
    ...annotativeRows
  };
}

export function buildTextPreviewEntity(
  entity: SimpleTextEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveTextPreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `Text ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "Text created through the geometry command core.",
    editableProperties: [...textPreviewProperties],
    properties
  };
}

/* ── MTEXT derivation and build ────────────────────── */

function attachmentLabel(value: number): string {
  const labels: Record<number, string> = {
    1: "Top Left", 2: "Top Center", 3: "Top Right",
    4: "Middle Left", 5: "Middle Center", 6: "Middle Right",
    7: "Bottom Left", 8: "Bottom Center", 9: "Bottom Right"
  };
  return labels[value] ?? "Top Left";
}

function directionLabel(value: number): string {
  const labels: Record<number, string> = {
    1: "Left to Right", 2: "Right to Left",
    3: "Top to Bottom", 4: "Bottom to Top", 5: "By Style"
  };
  return labels[value] ?? "Left to Right";
}

export function deriveMtextPreviewProperties(
  entity: SimpleMtextEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  // PR-9 — annotative rows. Spread last so they override any earlier
  // legacy "Annotative: No" placeholder in this map.
  const annotativeRows = deriveAnnotativeRows(properties);
  return {
    ...baseProperties,
    Color: properties["Color"] ?? "ByLayer",
    Layer: properties["Layer"] ?? "0",
    Lineweight: properties["Lineweight"] ?? "ByLayer",
    Transparency: properties["Transparency"] ?? "0",
    "Plot Style": properties["Plot Style"] ?? "ByColor",
    Material: properties["Material"] ?? "ByLayer",
    Contents: entity.contents,
    "Text style": entity.styleName,
    Height: formatNumber(entity.nominalHeight),
    Width: formatNumber(entity.referenceWidth),
    Attachment: attachmentLabel(entity.attachmentPoint),
    Direction: directionLabel(entity.drawingDirection),
    Rotation: formatNumber(entity.rotation),
    "Line spacing": formatNumber(entity.lineSpacingFactor),
    "Background fill": entity.backgroundFill ? "Yes" : "No",
    "Insertion X": formatNumber(entity.insertionPoint.x),
    "Insertion Y": formatNumber(entity.insertionPoint.y),
    ...annotativeRows
  };
}

export function buildMtextPreviewEntity(
  entity: SimpleMtextEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveMtextPreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `MText ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "MText created through the geometry command core.",
    editableProperties: [...mtextPreviewProperties],
    properties
  };
}

/* ── DIMENSION derivation and build ────────────────── */

const DIMENSION_SUBTYPE_LABELS: Record<number, string> = {
  0: "Linear",
  1: "Aligned",
  2: "Angular",
  3: "Diameter",
  4: "Radius",
  5: "Angular 3pt",
  6: "Ordinate"
};

// Decode the DIMENSION type bitfield's low nibble (group 70 & 0x0F) into the
// AutoCAD subtype label (e.g. "Linear"). Exported as the ONE TRUTH the typed
// property descriptors reuse for the Dimension "Subtype" display (bag-identical
// with the production single-select builder) — no re-implementation.
export function dimensionSubtypeLabel(dimensionType: number): string {
  const subtype = dimensionType & 0x0F;
  return DIMENSION_SUBTYPE_LABELS[subtype] ?? `Unknown (${subtype})`;
}

export function deriveDimensionPreviewProperties(
  entity: SimpleDimensionEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  const measurement = computeDimensionMeasurement(entity.extLine1Point, entity.extLine2Point);
  const result: Record<string, string> = {
    ...baseProperties,
    Color: properties["Color"] ?? "ByLayer",
    Layer: properties["Layer"] ?? "0",
    Linetype: properties["Linetype"] ?? "ByLayer",
    "Linetype Scale": properties["Linetype Scale"] ?? "1",
    Lineweight: properties["Lineweight"] ?? "ByLayer",
    Transparency: properties["Transparency"] ?? "0",
    "Plot Style": properties["Plot Style"] ?? "ByColor",
    Material: properties["Material"] ?? "ByLayer",
    Subtype: dimensionSubtypeLabel(entity.dimensionType),
    "Dimension style": entity.styleName,
    "Text override": entity.dimensionText,
    "Text height": formatNumber(entity.textHeight),
    "Text rotation": formatNumber(entity.textRotation),
    "Extension 1 X": formatNumber(entity.extLine1Point.x),
    "Extension 1 Y": formatNumber(entity.extLine1Point.y),
    "Extension 2 X": formatNumber(entity.extLine2Point.x),
    "Extension 2 Y": formatNumber(entity.extLine2Point.y),
    "Dimension line X": formatNumber(entity.dimensionLinePoint.x),
    "Dimension line Y": formatNumber(entity.dimensionLinePoint.y),
    "Third point X": entity.thirdPoint ? formatNumber(entity.thirdPoint.x) : "",
    "Third point Y": entity.thirdPoint ? formatNumber(entity.thirdPoint.y) : "",
    "Arc point X": entity.arcPoint ? formatNumber(entity.arcPoint.x) : "",
    "Arc point Y": entity.arcPoint ? formatNumber(entity.arcPoint.y) : "",
    "Oblique angle": formatNumber(entity.obliqueAngle),
    "Leader length": formatNumber(entity.leaderLength),
    "Anonymous block": entity.anonymousBlockName,
    Measurement: formatNumber(measurement),
    Angle: formatNumber(entity.linearAngle),
    // PR-9 — annotative rows.
    ...deriveAnnotativeRows(properties)
  };
  return result;
}

export function buildDimensionPreviewEntity(
  entity: SimpleDimensionEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveDimensionPreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `Dimension ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "Dimension created through the geometry command core.",
    editableProperties: [...dimensionPreviewProperties],
    properties
  };
}

/* ── MULTILEADER derivation and build ──────────────── */

export function deriveMultileaderPreviewProperties(
  entity: SimpleMultileaderEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  return {
    ...baseProperties,
    Color: properties["Color"] ?? "ByLayer",
    Layer: properties["Layer"] ?? "0",
    Lineweight: properties["Lineweight"] ?? "ByLayer",
    Transparency: properties["Transparency"] ?? "0",
    "Plot Style": properties["Plot Style"] ?? "ByColor",
    Material: properties["Material"] ?? "ByLayer",
    "Multileader style": entity.styleName,
    Contents: entity.textContents,
    "Text height": formatNumber(entity.textHeight),
    "Arrow X": formatNumber(entity.arrowPoint.x),
    "Arrow Y": formatNumber(entity.arrowPoint.y),
    "Landing X": formatNumber(entity.landingPoint.x),
    "Landing Y": formatNumber(entity.landingPoint.y),
    "Arrowhead size": formatNumber(entity.arrowheadSize),
    "Arrowhead block": properties["Arrowhead block"] ?? "",
    "Text attachment": properties["Text attachment"] ?? "1",
    "Text direction": properties["Text direction"] ?? "0",
    "Text frame": properties["Text frame"] ?? "No",
    "Line spacing style": properties["Line spacing style"] ?? "1",
    "Line spacing factor": properties["Line spacing factor"] ?? "1",
    "Leader color": properties["Leader color"] ?? "ByBlock",
    "Leader weight": properties["Leader weight"] ?? "ByBlock",
    "Leader type": properties["Leader type"] ?? "1",
    "Block name": properties["Block name"] ?? "",
    "Block scale": properties["Block scale"] ?? "1",
    "Block rotation": properties["Block rotation"] ?? "0",
    "Override flags": properties["Override flags"] ?? "0",
    "Landing gap": properties["Landing gap"] ?? "2",
    "Leader count": properties["Leader count"] ?? "1",
    // PR-9 — annotative rows.
    ...deriveAnnotativeRows(properties)
  };
}

export function buildMultileaderPreviewEntity(
  entity: SimpleMultileaderEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveMultileaderPreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `Multileader ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "Multileader created through the geometry command core.",
    editableProperties: [...multileaderPreviewProperties],
    properties
  };
}

/* ── REVISION CLOUD derivation and build ───────────── */

export function deriveRevisionCloudPreviewProperties(
  entity: SimpleRevisionCloudEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  return {
    ...baseProperties,
    Color: properties["Color"] ?? "ByLayer",
    Layer: properties["Layer"] ?? "0",
    Lineweight: properties["Lineweight"] ?? "ByLayer",
    Transparency: properties["Transparency"] ?? "0",
    "Plot Style": properties["Plot Style"] ?? "ByColor",
    Material: properties["Material"] ?? "ByLayer",
    Annotative: properties["Annotative"] ?? "No",
    "Cloud style": entity.revisionCloudStyle,
    "Approximate arc length": formatNumber(entity.approximateArcLength),
    // REVCLOUD-MODES — read-only creation-method row (full editing lands
    // with REVCLOUD-PROPS-EDITOR).
    "Creation mode": entity.creationMode,
    "Corner 1 X": formatNumber(entity.corner1.x),
    "Corner 1 Y": formatNumber(entity.corner1.y),
    "Corner 2 X": formatNumber(entity.corner2.x),
    "Corner 2 Y": formatNumber(entity.corner2.y),
    // Polygonal / Freehand / Object vertex chain — the renderer adapter
    // (`revisionCloudToRenderable`) prefers this JSON list over the
    // rectangular corner envelope, preserving non-rectangular shapes.
    ...(entity.vertices && entity.vertices.length >= 3
      ? {
          __revcloud_vertices: JSON.stringify(
            entity.vertices.map((v) => ({ x: v.x, y: v.y })),
          ),
        }
      : {})
  };
}

export function buildRevisionCloudPreviewEntity(
  entity: SimpleRevisionCloudEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveRevisionCloudPreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `Revision Cloud ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "Revision Cloud created through the geometry command core.",
    editableProperties: [...revisionCloudPreviewProperties],
    properties
  };
}

/* ── HATCH derivation and build ────────────────────── */

// Exported as the ONE TRUTH the typed property descriptors reuse for the Hatch
// "Hatch style" / "Pattern type" enum-label display (no re-implementation).
export function hatchStyleLabel(value: number): string {
  const labels: Record<number, string> = { 0: "Normal", 1: "Outer", 2: "Ignore" };
  return labels[value] ?? "Normal";
}

// N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2 round 6) — an out-of-table
// DXF group 76 code (a vendor/malformed value outside {0,1,2}) falls back to
// the RAW INTEGER as a string, NOT a hardcoded "Predefined" default. This
// matters because the Properties panel's whole-draft `properties/apply`
// (App.tsx `propertyDraft`, re-seeded from EVERY visible row on each apply —
// not only the field the user touched) resubmits "Pattern type"'s DISPLAYED
// label on ANY unrelated edit to the SAME hatch; the legacy translator's own
// "Pattern type" case decodes EITHER a recognised label OR a raw integer
// (`propertyTranslator.ts`, `containers.ts`'s `decodePatternTypeValue`
// mirrors it) — so round-tripping the unrecognised code back out AS that
// same raw integer string means an unrelated edit reproduces the ORIGINAL
// out-of-table code instead of silently coercing it to 1 (Predefined) — a
// real DXF group-76 data-loss risk a hardcoded fallback would reintroduce on
// every unrelated Properties-panel apply (CLAUDE.md "preserve source, never
// infer"; ezdxf / the DXF reference do not constrain group 76 to {0,1,2} —
// AutoCAD itself only ever writes those three, but a third-party/vendor
// writer is not guaranteed to).
export function patternTypeLabel(value: number): string {
  const labels: Record<number, string> = { 0: "User defined", 1: "Predefined", 2: "Custom" };
  return labels[value] ?? String(value);
}

export function deriveHatchPreviewProperties(
  entity: SimpleHatchEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  return {
    ...baseProperties,
    Color: properties["Color"] ?? "ByLayer",
    Layer: properties["Layer"] ?? "0",
    Lineweight: properties["Lineweight"] ?? "ByLayer",
    Transparency: properties["Transparency"] ?? "0",
    "Plot Style": properties["Plot Style"] ?? "ByColor",
    Material: properties["Material"] ?? "ByLayer",
    Pattern: entity.patternName,
    Scale: formatNumber(entity.patternScale),
    Angle: formatNumber(entity.patternAngle),
    Associative: entity.associative ? "Yes" : "No",
    "Hatch style": hatchStyleLabel(entity.hatchStyle),
    "Pattern type": patternTypeLabel(entity.patternType),
    // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR — `doubleLine` is not a field of the
    // creation-time `SimpleHatchEntity` (the HATCH command never seeds it;
    // AutoCAD's own HATCH command prompt sequence has no interactive
    // double-line prompt either — Double is a Hatch-Edit/Properties-only
    // toggle), so this mirrors the Color/Layer/… idiom just above: take the
    // caller override when supplied, else the AutoCAD default (No).
    Double: properties["Double"] ?? "No",
    // PR-9 — annotative rows.
    ...deriveAnnotativeRows(properties)
  };
}

export function buildHatchPreviewEntity(
  entity: SimpleHatchEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveHatchPreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `Hatch ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "Hatch created through the geometry command core.",
    editableProperties: [...hatchPreviewProperties],
    properties
  };
}

/* ── BLOCK INSERT derivation and build ─────────────── */

/** Lightweight BlockRecord projection the preview builder consumes. The full
 * schema lives in `@dxf-ai-editor/drawing-model`; we accept only the fields we
 * actually render so the command-engine package does not take a hard
 * dependency on every schema shape. */
export interface BlockRecordProjection {
  insertionUnits?: number;
  explodable?: boolean;
  scalable?: "uniform" | "non-uniform";
  description?: string;
}

/** Context driving the "Block Unit" / "Block Unit Factor" / Description /
 * Allow-* rows of the INSERT preview. (#248 re-audit, #301 r2) */
export interface BlockInsertPreviewContext {
  blockRecord?: BlockRecordProjection;
  drawingInsunits?: number;
  /** The unit-factor multiplier that was ACTUALLY applied to the insert's
   * scaleX/Y/Z. Callers that disable auto-scaling (`applyUnitFactor: false`)
   * must pass `1` here so the palette mirrors the applied multiplier instead
   * of recomputing the conversion from the raw unit pair. When omitted, the
   * factor is derived from `blockRecord.insertionUnits` × `drawingInsunits`
   * (callers that always apply the conversion). (#301 r2) */
  appliedUnitFactor?: number;
}

export function deriveBlockInsertPreviewProperties(
  entity: SimpleBlockInsertEntity,
  properties: Record<string, string> = {},
  context: BlockInsertPreviewContext = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  const record = context.blockRecord;
  const blockInsunits = record?.insertionUnits ?? 0;
  const drawingInsunits = context.drawingInsunits ?? 0;
  // AutoCAD's read-only "Unit factor" row — the block-units → drawing-units
  // conversion (how many drawing units one block unit becomes), i.e. the
  // multiplier the INSERT auto-scale applies. Delegates to the canonical
  // `drawing-model/blockUnits.ts` table so every INSUNITS code (incl.
  // 21 = US Survey Feet) resolves; the deleted local table stopped at 20
  // and inverted the ratio (drawing/block instead of block/drawing).
  // (#248 re-audit)
  //
  // INVARIANT (#301 r2): the displayed "Block Unit Factor" must equal the
  // scale multiplier the insert ACTUALLY received. When the caller supplies
  // `appliedUnitFactor` (e.g. forced to 1 by `applyUnitFactor: false`), it
  // wins over the unit-pair recomputation — otherwise the palette would
  // report a conversion that was never applied to the geometry.
  const unitFactor =
    context.appliedUnitFactor ??
    computeBlockUnitFactor(drawingInsunits, blockInsunits);
  // Hyperlink is a user-editable override; we prefer the inbound properties
  // map (which may already carry an edited value) and fall back to an empty
  // string so the field stays visible in the palette.
  const hyperlink = properties["Hyperlink"] ?? "";
  return {
    ...baseProperties,
    Name: entity.blockName,
    "Insertion X": formatNumber(entity.insertionPoint.x),
    "Insertion Y": formatNumber(entity.insertionPoint.y),
    "Scale X": formatNumber(entity.scaleX),
    "Scale Y": formatNumber(entity.scaleY),
    "Scale Z": formatNumber(entity.scaleZ),
    Rotation: formatNumber(entity.rotation),
    "Column count": String(entity.columnCount),
    "Row count": String(entity.rowCount),
    "Column spacing": formatNumber(entity.columnSpacing),
    "Row spacing": formatNumber(entity.rowSpacing),
    Hyperlink: hyperlink,
    Description: record?.description ?? "",
    "Block Unit": String(blockInsunits),
    "Block Unit Factor": formatNumber(unitFactor),
    "Allow Exploding": record?.explodable === false ? "No" : "Yes",
    "Allow Non-Uniform Scale":
      record?.scalable === "uniform" ? "No" : "Yes",
    // PR-9 — annotative rows.
    ...deriveAnnotativeRows(properties),
  };
}

export function buildBlockInsertPreviewEntity(
  entity: SimpleBlockInsertEntity,
  options: {
    label?: string;
    summary?: string;
    properties?: Record<string, string>;
    /** BlockRecord + drawing `$INSUNITS` pair driving the "Block Unit" /
     * "Block Unit Factor" / Description / Allow-* rows. Optional —
     * callers without record context render the Unitless defaults
     * (factor 1). Callers that disabled auto-scaling must pass
     * `appliedUnitFactor: 1` so the palette mirrors the applied
     * multiplier (see `BlockInsertPreviewContext`). (#248 re-audit,
     * #301 r2) */
    context?: BlockInsertPreviewContext;
  } = {}
): EntityPreview {
  const properties = deriveBlockInsertPreviewProperties(
    entity,
    options.properties ?? {},
    options.context ?? {}
  );
  return {
    id: entity.id,
    label: options.label ?? `Block ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "Block INSERT created through the geometry command core.",
    editableProperties: [...blockInsertPreviewProperties],
    properties
  };
}

/* ── RECTANGULAR ASSOCIATIVE ARRAY derivation and build ─ */

export function deriveArrayRectPreviewProperties(
  entity: SimpleArrayRectEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  return {
    ...baseProperties,
    Rows: String(entity.rowCount),
    Columns: String(entity.columnCount),
    "Row spacing": formatNumber(entity.rowSpacing),
    "Column spacing": formatNumber(entity.columnSpacing),
    "Base X": formatNumber(entity.basePoint.x),
    "Base Y": formatNumber(entity.basePoint.y),
    "Axis angle": formatNumber(entity.axisAngle),
    Associative: entity.associative ? "Yes" : "No"
  };
}

export function buildArrayRectPreviewEntity(
  entity: SimpleArrayRectEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveArrayRectPreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `Rectangular Associative Array ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "Rectangular Associative Array created through the geometry command core.",
    editableProperties: [...arrayRectPreviewProperties],
    properties
  };
}

/* ── VIEWPORT derivation and build ─────────────────── */

export function deriveViewportPreviewProperties(
  entity: SimpleViewportEntity,
  _properties: Record<string, string> = {}
): Record<string, string> {
  return {
    "Display lock": entity.displayLocked ? "Yes" : "No",
    Scale: entity.scale,
    Layer: entity.layer,
    "Shade plot": entity.shadePlot,
    "Center X": formatNumber(entity.center.x),
    "Center Y": formatNumber(entity.center.y),
    Width: formatNumber(entity.width),
    Height: formatNumber(entity.height),
    "View height": formatNumber(entity.viewHeight),
    "View center X": formatNumber(entity.viewCenterX),
    "View center Y": formatNumber(entity.viewCenterY),
    // @codex round-6 P2-3 — `View twist` (group 51) is a registry-derived OWNED plain
    // leaf advertised editable in `viewportPreviewProperties`, so this DERIVE factory
    // MUST emit its VALUE too (created viewport ⇒ 0, matching the import factory's
    // `formatNumber(viewTwistAngle ?? 0)`): else `buildViewportPreviewEntity` offers
    // the row + inverse snapshot with no backing bag key — the both-factories
    // advertised-without-emitted divergence (View twist was its 2nd occurrence).
    "View twist": formatNumber(entity.viewTwistAngle ?? 0)
  };
}

export function buildViewportPreviewEntity(
  entity: SimpleViewportEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveViewportPreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `Viewport ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "Viewport created through the geometry command core.",
    editableProperties: [...viewportPreviewProperties],
    properties
  };
}

/* ── ELLIPSE derivation and build ───────────────────── */

export function deriveEllipsePreviewProperties(
  entity: SimpleEllipseEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);
  return {
    ...baseProperties,
    "Center X": formatNumber(entity.center.x),
    "Center Y": formatNumber(entity.center.y),
    // SL-15 — carry the FULL 3D geometry so a preview exploded out of a tilted /
    // elevated INSERT is byte-identical to the reconciled document (shell↔doc
    // parity). Emitted ONLY when present so a planar ellipse stays byte-identical
    // (no spurious "Center Z": "0" / extrusion rows), and NOT added to the
    // editable `ellipsePreviewProperties` roster (the typed-descriptor census
    // pins that roster to curves.ts descriptors; the palette Z-edit arm is a
    // separate Category-C slice).
    ...(entity.center.z !== undefined
      ? { "Center Z": formatNumber(entity.center.z) }
      : {}),
    "Major axis X": formatNumber(entity.majorAxisEndpoint.x),
    "Major axis Y": formatNumber(entity.majorAxisEndpoint.y),
    ...(entity.majorAxisEndpoint.z !== undefined
      ? { "Major axis Z": formatNumber(entity.majorAxisEndpoint.z) }
      : {}),
    Ratio: formatNumber(entity.ratio),
    "Start parameter": formatNumber(entity.startParam),
    "End parameter": formatNumber(entity.endParam),
    ...(entity.extrusion !== undefined
      ? {
          "Extrusion X": formatNumber(entity.extrusion.x),
          "Extrusion Y": formatNumber(entity.extrusion.y),
          "Extrusion Z": formatNumber(entity.extrusion.z),
        }
      : {}),
  };
}

export function buildEllipsePreviewEntity(
  entity: SimpleEllipseEntity,
  options: { label?: string; summary?: string; properties?: Record<string, string> } = {}
): EntityPreview {
  const properties = deriveEllipsePreviewProperties(entity, options.properties ?? {});
  return {
    id: entity.id,
    label: options.label ?? `Ellipse ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary: options.summary ?? "Ellipse created through the geometry command core.",
    editableProperties: [...ellipsePreviewProperties],
    properties,
  };
}

/* ── Generic extended property change applier ──────── */

export type ExtendedPropertyChangeResult = {
  entity: EntityPreview;
  patch: ExtendedGeometryPatch | null;
  appliedChanges: SimpleGeometryPropertyChange[];
  skipped: SimpleGeometryPropertySkip[];
  summary: string;
};

